import styled from '@emotion/styled'
import * as Popover from '@radix-ui/react-popover'
import { Command } from 'cmdk'
import { ReactElement, ReactNode, useState } from 'react'

import { Enumerable } from '@bemlo/utils'

import { PopoverComboboxOptions } from './Options'
import { theme } from '../../../constants'
import { useTranslations } from '../../../translate'
import { BadgeColor } from '../../Badge'
import { IconName } from '../../Icon'
import { panelStyles } from '../styles'
import { FilterInput } from './FilterInput'

const Panel = styled(Command)(panelStyles)

const PopoverContent = styled(Popover.Content, {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ compact }) => ({
  width: compact ? 'fit-content' : 280,
  zIndex: theme.zIndex.popover,
}))

type Item<T extends string | number> = {
  badgeColor?: BadgeColor
  icon?: IconName
  user?: { name: string; avatarUrl?: string }
  label: string
  value: T
}

type ItemGroup<T extends string | number> = {
  label?: string
  items: Item<T>[]
}

type PopoverComboboxBoxProps<T extends string | number> = {
  actionsSlot?: ReactNode
  alignContent?: 'start' | 'center' | 'end'
  compact?: boolean
  filterPlaceholder?: string
  groups: Enumerable<ItemGroup<T>>
  /**
   * Use the `isOpen` prop to control the open state manually.
   */
  isOpen?: boolean
  noItemsFoundLabel?: string
  onOpenChange?: (open: boolean) => void
  onSelect: (value: T) => void
  trigger: ReactElement
  type: 'multiSelect' | 'singleSelect' | 'trigger'
  value?: Enumerable<T> | null | undefined
  defaultOpen?: boolean
  closeOnSelect?: boolean
}

export const PopoverCombobox = <T extends string | number>({
  actionsSlot,
  alignContent,
  closeOnSelect,
  compact,
  defaultOpen = false,
  filterPlaceholder,
  groups,
  isOpen,
  noItemsFoundLabel,
  onOpenChange,
  onSelect,
  trigger,
  type,
  value,
}: PopoverComboboxBoxProps<T>) => {
  const t = useTranslations()
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen)

  const setIsOpen = (open: boolean) => {
    if (isOpen === undefined) {
      setInternalIsOpen(open)
    }
    onOpenChange?.(open)
  }

  return (
    <Popover.Root open={isOpen ?? internalIsOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <PopoverContent
          sideOffset={4}
          align={alignContent ?? 'start'}
          compact={compact}
        >
          <Panel>
            <FilterInput
              autoFocus
              placeholder={filterPlaceholder ?? t.ui.filter_ellipsis()}
            />
            <PopoverComboboxOptions
              actionsSlot={actionsSlot}
              groups={groups}
              noItemsFoundLabel={noItemsFoundLabel}
              onSelect={(value) => {
                if (closeOnSelect) setIsOpen(false)
                onSelect(value)
              }}
              type={type}
              value={value}
            />
          </Panel>
        </PopoverContent>
      </Popover.Portal>
    </Popover.Root>
  )
}

export type { Item, ItemGroup, PopoverComboboxBoxProps }
